import { useEffect } from "react";
import { useBlocker } from "react-router-dom";

/**
 * Warn before leaving the league wizard with unsaved work.
 *
 * Season Setup generates the whole round plan client-side and holds it in the
 * form until Save draft sends it — so an operator who generates rounds and then
 * navigates away loses every one of them with no warning and nothing to
 * recover. Until the draft is persisted as it is built, this is what stands
 * between that work and a mis-click.
 *
 * Covers both exits: `useBlocker` for in-app navigation (needs the data router,
 * which this app uses) and `beforeunload` for closing, reloading, or following
 * a link out. The browser dialog's wording is the browser's own; only the
 * in-app one can be phrased.
 */
export function useUnsavedChangesGuard(when: boolean) {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      when && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (!when) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      // Required for Chrome to show its dialog; the message is ignored.
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [when]);

  // A blocker that outlives the condition would strand the operator on a page
  // they're allowed to leave — reset as soon as the work is saved.
  useEffect(() => {
    if (!when && blocker.state === "blocked") blocker.reset();
  }, [when, blocker]);

  return {
    /** True while a navigation is held pending the operator's answer. */
    isBlocked: blocker.state === "blocked",
    /** Discard the work and continue to where they were going. */
    confirmLeave: () => blocker.proceed?.(),
    /** Stay put. */
    cancelLeave: () => blocker.reset?.(),
  };
}
